import _ from 'lodash';
import { useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';

import SupplierTermsBeautifier from '@/utils/SupplierTermsBeautifier';
import { useTranslation } from 'react-i18next';
import { LTR, SUPPORTED_LANGUAGES } from 'arbitrip-common/translation/const';
import TranslationToggleNotice from '@/components/general/TranslationToggleNotice.jsx';
import { useElalCompany } from '@/hooks/useElalCompany';

const CONTENT_LOADER_WIDTH = 761;
const CONTENT_LOADER_HEIGHT = 80;
const MAX_CHARS = 325;

const TermsOfSupplier = ({ deal, reservation, initializing }) => {
    const { t, i18n } = useTranslation();
    const isElal = useElalCompany();

    const [isExpanded, setIsExpanded] = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);

    const hotelSupplierTerms = _.get(deal, 'original.details.hotel_supplier_terms', 'No special terms');
    const translated = _.get(reservation, 'supplier_remarks_translations');
    const language = i18n.language || SUPPORTED_LANGUAGES.EN;
    const translatedHtml = translated ? _.get(translated, language) : null;

    const baseTerms = _.get(deal, 'chooseprod.dca.info', '').trim() || hotelSupplierTerms;
    const displayTerms = useMemo(
        () => (showOriginal || !translatedHtml ? baseTerms : translatedHtml),
        [showOriginal, translatedHtml, baseTerms],
    );

    let specialTerms = SupplierTermsBeautifier.extract(displayTerms);
    const isHTML = /<\/?[a-z][\s\S]*>/i.test(specialTerms);

    const isTooLong = specialTerms.length > MAX_CHARS;

    const truncatedContent = isTooLong && !isExpanded ? `${specialTerms.slice(0, MAX_CHARS).trim()}...` : specialTerms;

    return (
        <div className="terms-of-supplier">
            <div className="title">{t('TermsOfSupplierSection.Title')}</div>
            {initializing ? (
                <ContentLoader
                    width={CONTENT_LOADER_WIDTH}
                    height={CONTENT_LOADER_HEIGHT}
                    viewBox={`0 0 ${CONTENT_LOADER_WIDTH} ${CONTENT_LOADER_HEIGHT}`}
                >
                    <rect x="5" y="5" rx="0" ry="0" width="720" height="13" />
                    <rect x="5" y="27" rx="0" ry="0" width="660" height="13" />
                    <rect x="5" y="49" rx="0" ry="0" width="700" height="13" />
                </ContentLoader>
            ) : (
                <>
                    <TranslationToggleNotice
                        translatedExists={!!translatedHtml}
                        showOriginal={showOriginal}
                        onToggle={() => setShowOriginal((v) => !v)}
                        style={{ backgroundColor: '#fff', marginBottom: 16 }}
                    />
                    <div className={`terms-wrap`} dir={showOriginal ? LTR : i18n.dir()}>
                        {isHTML ? (
                            <span className="terms" dangerouslySetInnerHTML={{ __html: truncatedContent }} />
                        ) : (
                            <span className="terms">{truncatedContent}</span>
                        )}

                        {isTooLong && (
                            <button
                                className="text-button"
                                aria-label={
                                    isExpanded
                                        ? t('TermsOfSupplierSection.ReadLess')
                                        : t('TermsOfSupplierSection.ReadMore')
                                }
                                onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
                            >
                                {isExpanded
                                    ? t('TermsOfSupplierSection.ReadLess')
                                    : t('TermsOfSupplierSection.ReadMore')}
                            </button>
                        )}
                    </div>
                    {isElal && (
                        <div className="disclaimer" dir={i18n.dir()}>
                            {t('TermsOfSupplierSection.Disclaimer')}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TermsOfSupplier;
